import { ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import type { StockAlertCloseAction, StockAlertRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';
import { ellipsisCell, TipText, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

type StatusFilter = 'NEW' | 'PROCESSING' | 'RESOLVED' | 'IGNORED' | 'ALL';

const LEVEL_META: Record<string, { color: string; text: string }> = {
  CRITICAL: { color: 'red', text: '严重' },
  WARN: { color: 'orange', text: '警告' },
  INFO: { color: 'blue', text: '提示' }
};

const TYPE_META: Record<string, { color: string; text: string }> = {
  STOCK_OUT: { color: 'red', text: '缺货' },
  LOW_STOCK: { color: 'orange', text: '低库存' },
  NEAR_EXPIRY: { color: 'gold', text: '近效期' },
  EXPIRED: { color: 'red', text: '已过期' },
  OVER_STOCK: { color: 'gold', text: '高储' },
  DULL_STOCK: { color: 'purple', text: '呆滞' },
  EMERGENCY_STOCK: { color: 'magenta', text: '应急储备不足' }
};

const STATUS_META: Record<string, { color: string; text: string }> = {
  NEW: { color: 'blue', text: '待处理' },
  PROCESSING: { color: 'processing', text: '处理中' },
  RESOLVED: { color: 'green', text: '已解决' },
  IGNORED: { color: 'default', text: '已忽略' }
};

/**
 * 库存/效期预警（`wl_stock_alert`）：阈值来自「库存规则」（`cfg_stock_rule`），
 * 支持手动扫描、按状态/类型/级别筛选，以及已解决/忽略闭环。
 */
export default function StockAlertOps() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('NEW');
  const [alertType, setAlertType] = useState('');
  const [alertLevel, setAlertLevel] = useState('');

  const summary = useQuery({
    queryKey: ['ops', 'stock-alert', 'summary'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.stockAlert.summary()
  });

  const list = useInfiniteQuery({
    queryKey: ['ops', 'stock-alert', 'list', status, alertType, alertLevel],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.stockAlert.list({
        lastId: pageParam ?? undefined,
        size: PAGE_SIZE,
        alertStatus: status === 'ALL' ? '' : status,
        alertType,
        alertLevel
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const scan = useMutation({
    mutationFn: () => hq.stockAlert.scan(30),
    onSuccess: async (result) => {
      message.success(
        `扫描完成：低库存 ${result.lowStock}、效期 ${result.expiry}，写入 ${result.upserted} 条`
      );
      await queryClient.invalidateQueries({ queryKey: ['ops', 'stock-alert'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '扫描失败，请稍后重试');
    }
  });

  const close = useMutation({
    mutationFn: (payload: { id: string; action: StockAlertCloseAction }) =>
      hq.stockAlert.close(payload.id, payload.action),
    onSuccess: async () => {
      message.success('预警已处理');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'stock-alert'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '处理失败，请稍后重试');
    }
  });

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const error = list.error instanceof ApiError ? list.error.friendlyMessage : null;
  const stats = summary.data;

  const columns: ColumnsType<StockAlertRow> = [
    {
      title: '级别',
      dataIndex: 'alertLevel',
      width: 90,
      render: (value: string) => {
        const meta = LEVEL_META[value] ?? { color: 'default', text: value };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '类型',
      dataIndex: 'alertType',
      width: 100,
      render: (value: string) => {
        const meta = TYPE_META[value] ?? { color: 'default', text: value };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '物资',
      ellipsis: { showTitle: false },
      // 单行展示：编码收进悬浮提示，避免行高被拉高
      render: (_, row) => <TipText text={row.materialName ?? '-'} tip={`${row.materialName ?? '-'} · ${row.materialCode ?? '-'}`} />
    },
    {
      title: '仓库',
      dataIndex: 'warehouseName',
      width: 160,
      ellipsis: { showTitle: false },
      render: (value: string | null, row) => <TipText text={value || `仓库 ${row.warehouseId}`} />
    },
    {
      title: '触发值 / 阈值',
      width: 150,
      align: 'right',
      render: (_, row) => (
        <Typography.Text type={row.triggerValue < row.thresholdValue ? 'danger' : undefined}>
          {formatQty(row.triggerValue)} / {formatQty(row.thresholdValue)}
        </Typography.Text>
      )
    },
    { title: '预警内容', dataIndex: 'alertContent', ellipsis: { showTitle: false }, render: ellipsisCell('alertContent') },
    {
      title: '状态',
      dataIndex: 'alertStatus',
      width: 100,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { color: 'default', text: value };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '首次告警', dataIndex: 'firstAlertAt', width: 130, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '持续(分)',
      dataIndex: 'handleDurationMinutes',
      width: 90,
      align: 'right',
      render: (value: number) => (value > 0 ? value : '-')
    },
    {
      title: '操作',
      width: 150,
      fixed: 'right',
      render: (_, row) =>
        row.alertStatus === 'NEW' || row.alertStatus === 'PROCESSING' ? (
          <Space size={0}>
            <Popconfirm
              title="标记为已解决？"
              okText="已解决"
              cancelText="取消"
              onConfirm={() => close.mutate({ id: row.id, action: 'RESOLVED' })}
            >
              <Button type="link" size="small" loading={close.isPending}>
                已解决
              </Button>
            </Popconfirm>
            <Popconfirm
              title="忽略该预警？"
              okText="忽略"
              cancelText="取消"
              onConfirm={() => close.mutate({ id: row.id, action: 'IGNORED' })}
            >
              <Button type="link" size="small" danger loading={close.isPending}>
                忽略
              </Button>
            </Popconfirm>
          </Space>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        )
    }
  ];

  return (
    <Card
      className="page-card"
      title="库存预警"
      extra={
        <Space>
          <Button icon={<SyncOutlined />} type="primary" loading={scan.isPending} onClick={() => scan.mutate()}>
            立即扫描
          </Button>
          <Button
            icon={<ReloadOutlined />}
            loading={list.isFetching || summary.isFetching}
            onClick={() => {
              void list.refetch();
              void summary.refetch();
            }}
          >
            刷新
          </Button>
        </Space>
      }
    >
      <Space wrap style={{ marginBottom: 12 }} size={12}>
        <Tag color="red">严重 {stats?.byLevel.CRITICAL ?? 0}</Tag>
        <Tag color="orange">警告 {stats?.byLevel.WARN ?? 0}</Tag>
        <Tag color="blue">提示 {stats?.byLevel.INFO ?? 0}</Tag>
        <Typography.Text type="secondary">未闭环合计 {stats?.total ?? 0} 条</Typography.Text>
      </Space>

      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented<StatusFilter>
          value={status}
          options={[
            { label: '待处理', value: 'NEW' },
            { label: '处理中', value: 'PROCESSING' },
            { label: '已解决', value: 'RESOLVED' },
            { label: '已忽略', value: 'IGNORED' },
            { label: '全部', value: 'ALL' }
          ]}
          onChange={(value) => setStatus(value)}
        />
        <Select
          value={alertType}
          style={{ width: 150 }}
          onChange={setAlertType}
          options={[
            { value: '', label: '全部类型' },
            { value: 'STOCK_OUT', label: '缺货' },
            { value: 'LOW_STOCK', label: '低库存' },
            { value: 'NEAR_EXPIRY', label: '近效期' },
            { value: 'EXPIRED', label: '已过期' },
            { value: 'OVER_STOCK', label: '高储' },
            { value: 'DULL_STOCK', label: '呆滞' },
            { value: 'EMERGENCY_STOCK', label: '应急储备不足' }
          ]}
        />
        <Select
          value={alertLevel}
          style={{ width: 140 }}
          onChange={setAlertLevel}
          options={[
            { value: '', label: '全部级别' },
            { value: 'CRITICAL', label: '严重' },
            { value: 'WARN', label: '警告' },
            { value: 'INFO', label: '提示' }
          ]}
        />
      </Space>

      {error ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message="加载失败" description={error} /> : null}

      <Table<StockAlertRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无预警，可点击「立即扫描」按库存规则检查' }}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>
    </Card>
  );
}
